import { useEffect, useMemo } from 'react';

import { useHistory } from 'react-router-dom';

import {
  useModeContext,
  UserMode,
  UserModeRoutes,
} from '~/lib/contexts/ModeContext';
import useLocalStorage from '~/lib/hooks/useLocalStorage';
import usePortalAccess, { PortalAccess } from '~/lib/hooks/usePortalAccess';
import { Path } from '~/routes/constants';

const resolveUserMode = (
  { admin, maud, ops }: PortalAccess,
  hasSinglePortal: boolean,
  intent: string | null,
): UserMode => {
  const caselessIntent = intent?.toLowerCase() ?? '';
  const candidates: [UserMode, boolean, string][] = [
    [UserMode.ADMIN, admin, UserMode.ADMIN.toLowerCase()],
    [UserMode.MAUD, maud, UserMode.MAUD.toLowerCase()],
    [UserMode.OPS, ops, Path.OPS],
  ];

  const match = candidates.find(
    ([, hasAccess, intentMarker]) =>
      hasAccess && (hasSinglePortal || caselessIntent.includes(intentMarker)),
  );

  return match?.[0] ?? UserMode.UNKNOWN;
};

const useUserMode = () => {
  const { mode, setMode } = useModeContext();
  const { storedValue: intent, removeValue: removeIntent } =
    useLocalStorage<string>('intent', Path.HOME);
  const history = useHistory();

  const permissions = usePortalAccess();
  const portalCount = Object.values(permissions).filter(Boolean).length;
  const hasSinglePortal = portalCount === 1;

  const shouldRenderPortal = useMemo<boolean>(
    () => portalCount > 1 && mode === UserMode.UNKNOWN,
    [portalCount, mode],
  );

  useEffect(() => {
    const { admin, maud, ops } = permissions;
    const hasCurrentModeAccess = {
      [UserMode.ADMIN]: admin,
      [UserMode.MAUD]: maud,
      [UserMode.OPS]: ops,
      [UserMode.UNKNOWN]: false,
    }[mode];

    if (hasCurrentModeAccess || !(admin || maud || ops)) return;

    const userMode = resolveUserMode(permissions, hasSinglePortal, intent);

    setMode(userMode);

    if (userMode !== UserMode.UNKNOWN && intent) {
      history.push(intent);
      removeIntent();
    } else {
      history.push(UserModeRoutes[userMode]);
    }
  }, [permissions]);

  return { mode, permissions, shouldRenderPortal };
};

export default useUserMode;
